import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AILoadingAnimation from "../components/AILoadingAnimation";
import { SubmissionHandler } from '../components/submitHandlers';
import useNavigationWarning from '../hooks/useNavigationWarning';

const isDevelopment = process.env.REACT_APP_ENV === 'development';
const API_URL = isDevelopment
  ? 'http://localhost:5000/flask'
  : (process.env.REACT_APP_API_URL || 'https://your-aws-alb-dns-name');

const BETA_TESTING = (process.env.REACT_APP_BETA_TESTING || 'false').toLowerCase() === 'true';

const ProcessingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [betaMessage, setBetaMessage] = useState('');
  const [processedFormData, setProcessedFormData] = useState(null); // Add this state
  const [hasProcessed, setHasProcessed] = useState(false); // Prevent double processing
  const [userEmail, setUserEmail] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [completionProgress, setCompletionProgress] = useState(0);
  const [resultData, setResultData] = useState(null);
  // Warn users before leaving while processing - this is critical!
  // Allow forward navigation to skills-selection or results (the next steps)
  useNavigationWarning(!error, 'Your resume is being processed. Leaving now will cancel the process. Are you sure?', {
    allowPaths: ['/skills-selection', '/results'],
  });

  // Updated to handle both formData (legacy) and formDataEntries (new)
  const { formData, formDataEntries, activeModal } = location.state || {};

  // First useEffect: Reconstruct FormData and store in state
  useEffect(() => {
    if ((!formData && !formDataEntries) || !activeModal || hasProcessed) {
      if (!formData && !formDataEntries) {
        navigate('/');
      }
      return;
    }

    // Reconstruct FormData if we received formDataEntries
    let actualFormData = formData;
    if (formDataEntries && !formData) {
      actualFormData = new FormData();
      // console.log("Reconstructing FormData from entries:", formDataEntries);
      for (const [key, value] of formDataEntries) {
        // console.log(`Adding to FormData: ${key} =`, value);
        actualFormData.append(key, value);
      }

      // Debug: Log the reconstructed FormData
      // console.log("Reconstructed FormData entries:");
      // for (let [key, value] of actualFormData.entries()) {
      //   // console.log(`${key}:`, value);
      // }
    }

    // In ProcessingPage.js - add this after line 56 (after the debug logging)
    if (actualFormData.has('user_email')) {
      setUserEmail(actualFormData.get('user_email'));
    }
    // Store the FormData in state
    setProcessedFormData(actualFormData);
    setHasProcessed(true);
  }, [formData, formDataEntries, activeModal, navigate, hasProcessed]);
  // Add this new useEffect
  useEffect(() => {
    // Only proceed if we have completionProgress at 100%, isComplete is true, and we have resultData
    if (completionProgress === 120 && isComplete && resultData) {
      // This is the same navigation logic you had before
      if (resultData.skills_analysis) {
        const missingSkills = resultData.skills_analysis.missing_skills_by_category;
        const hasSkills =
          (resultData.skills_analysis.is_tech_job && missingSkills && (
            (Array.isArray(missingSkills["Programming Languages"]) && missingSkills["Programming Languages"].length > 0) ||
            (Array.isArray(missingSkills["Applications"]) && missingSkills["Applications"].length > 0) ||
            (Array.isArray(missingSkills["Technical Skills"]) && missingSkills["Technical Skills"].length > 0)
          )) ||
          (!resultData.skills_analysis.is_tech_job && missingSkills &&
            Array.isArray(missingSkills["Non-Technical Skills"]) &&
            missingSkills["Non-Technical Skills"].length > 0);
        if (hasSkills && resultData.resume_data) {
          // Navigate to skills selection page
          navigate('/skills-selection', {
            state: {
              topSkillsData: resultData.skills_analysis,
              resumeDataBeforeSkills: resultData.resume_data,
              metrics: resultData.metrics || null,
              similarJobTitles: resultData.similar_job_titles || [],
              isStreamlineFlow: activeModal === 'streamline',
              jobTitle: resultData.skills_analysis?.job_title || '',
              userEmail: userEmail,
              emailSendToken: resultData.email_send_token,  
            }
          });
          return;
        }
      }

      // No skills to select, go directly to results
      navigate('/results', {
        state: {
          resumeData: resultData.resume_data,
          pdfData: resultData.pdf_data,
          filename: resultData.filename,
          metrics: resultData.metrics || null,
          similarJobTitles: resultData.similar_job_titles || [],
          topSkills: resultData.top_skills || [],
          betaMessage: betaMessage,
          jobTitle: resultData.skills_analysis?.job_title || '',
          userEmail: userEmail,
          emailSendToken: resultData.email_send_token,
        }
      });
    }
  }, [completionProgress, isComplete, resultData, navigate, activeModal, betaMessage, userEmail]);
  // Second useEffect: Process the submission once FormData is ready
  useEffect(() => {
    if (!processedFormData || !activeModal) {
      return;
    }

    const processSubmission = async () => {
      try {



        // console.log("Processing submission with FormData:", processedFormData);

        // Add one more debug log right before submission
        // console.log("Final FormData check:");
        // for (let [key, value] of processedFormData.entries()) {
        //   console.log(`${key}:`, value);
        // }

        const result = await SubmissionHandler.handleSubmission(activeModal, processedFormData, API_URL);
        setResultData(result);
        if (BETA_TESTING && result.remaining_uses !== undefined) {
          setBetaMessage(`You have ${result.remaining_uses} beta uses remaining`);
        }

        if (result.status === 'success') {
          // Check if we need to show skills selection
          setIsComplete(true);
          // replace timeout with an if statement 
          setResultData(result);
        } else {
          setError(result.error || 'An error occurred during processing');
        }
      } catch (error) {
        console.error("Error in processSubmission:", error);
        setError('Failed to process submission');
      }
    };

    processSubmission();
  }, [processedFormData, activeModal, navigate, betaMessage]);

  useEffect(() => {
    // Add a style tag directly to the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .app-container {
        background: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-image: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-attachment: fixed !important;
      }
      
      .app-container::before,
      .app-container::after {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  if ((!formData && !formDataEntries) || !activeModal) {
    return null;
  }

  // In src/pages/ProcessingPage.js
  // Update the return statement with this code:

  return (
    <div className="homepage">
      {error ? (
        <div className="result-container error">
          {error}
          <button
            className="submit-button"
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Return to Home
          </button>
        </div>
      ) : (
        <div className="result-container loading" style={{
          maxWidth: '600px',
          margin: '0 auto',
          background: 'transparent', /* Make sure there's no background */
          border: 'none', /* Remove any border */
          boxShadow: 'none' /* Remove any shadow */
        }}>
          <AILoadingAnimation
            isComplete={isComplete}
            completionProgress={completionProgress}
            setCompletionProgress={setCompletionProgress} />
        </div>
      )}
    </div>
  );
};

export default ProcessingPage;
